import React, { useEffect, useState } from "react";
import { CheckCircle2, Loader2, X } from "lucide-react";
import { toast } from "../hooks/use-toast";
import { hearOptions } from "../mock";
import { api } from "../api";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";

const empty = {
  name: "", phone: "", email: "", address: "", town: "", postcode: "",
  message: "", hear: "",
};

const QuoteCard = () => {
  const [form, setForm] = useState(empty);
  const [photo, setPhoto] = useState(null); // { path, filename }
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    const onPrefill = (e) => setForm((f) => ({ ...f, message: e.detail }));
    window.addEventListener("prefill-quote", onPrefill);
    return () => window.removeEventListener("prefill-quote", onPrefill);
  }, []);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast({ title: "Photo too large", description: "Please choose an image under 10MB." });
      e.target.value = "";
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/uploads/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhoto({ path: data.path, filename: data.filename });
    } catch (err) {
      toast({ title: "Photo upload failed", description: err?.response?.data?.detail || "Please try again or send it without a photo." });
      e.target.value = "";
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email) {
      toast({ title: "Please complete required fields", description: "Name, contact number and email are required." });
      return;
    }
    if (uploading) {
      toast({ title: "Photo still uploading", description: "One moment - your photo is nearly there." });
      return;
    }
    setSending(true);
    try {
      await api.post("/enquiries", { ...form, location: photo?.filename || "", photo_path: photo?.path || "" });
      toast({ title: "Thank you! Your request has been received.", description: "We've emailed you a confirmation and Charlie and the team will be in touch shortly." });
      setForm(empty);
      setPhoto(null);
      const input = document.getElementById("quote-photo-input");
      if (input) input.value = "";
    } catch (err) {
      toast({ title: "Something went wrong", description: "Please call us on 01501 760051 or try again." });
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="quote-form-card">
      <div className="banner-shape text-center py-5 px-4 bg-fire" style={{ "--shape-fill": "#F2F2F2" }}>
        <h3 className="text-charcoal font-normal text-[28px] md:text-[28px]">
          <span className="text-white font-bold">FREE</span> Quote Today
        </h3>
      </div>
      <form id="quote-form" onSubmit={submit} className="p-4 md:p-5 pb-6" style={{ backgroundColor: "#F2F2F2" }} data-testid="quote-form">
        <div className="grid sm:grid-cols-2 gap-[10px]">
          <Input value={form.name} onChange={set("name")} placeholder="Name" data-testid="quote-name-input" />
          <Input value={form.phone} onChange={set("phone")} placeholder="Contact Number" data-testid="quote-phone-input" />
        </div>
        <div className="mt-[14px]">
          <Input type="email" value={form.email} onChange={set("email")} placeholder="Email" data-testid="quote-email-input" />
        </div>
        <div className="grid grid-cols-5 gap-[10px] mt-[14px]">
          <div className="col-span-3"><Input value={form.address} onChange={set("address")} placeholder="First Line Address" data-testid="quote-address-input" /></div>
          <div className="col-span-2"><Input value={form.town} onChange={set("town")} placeholder="Town" data-testid="quote-town-input" /></div>
        </div>
        <div className="mt-[14px]">
          <Input value={form.postcode} onChange={set("postcode")} placeholder="Postcode" data-testid="quote-postcode-input" />
        </div>
        <div className="mt-[14px]">
          <Textarea value={form.message} onChange={set("message")} rows={3} placeholder="Message" data-testid="quote-message-input" />
        </div>

        <hr className="my-4 border-[#DDDADA]" />
        <p className="text-charcoal text-[14px] mb-3">
          Please upload a picture of where you would like your stove located. This will enable our team to
          pre-assess the area and installation.
        </p>
        <label className="block">
          <span className="block text-sm text-charcoal mb-2">Stove Location</span>
          <input
            id="quote-photo-input"
            type="file"
            accept="image/*"
            onChange={onFile}
            disabled={uploading}
            className="file-input block w-full text-sm text-charcoal cursor-pointer"
            data-testid="quote-file-input"
          />
        </label>
        {uploading && (
          <p className="flex items-center gap-2 text-sm text-charcoal mt-2" data-testid="quote-photo-uploading">
            <Loader2 size={16} className="animate-spin text-fire" /> Uploading photo...
          </p>
        )}
        {photo && !uploading && (
          <p className="flex items-center gap-2 text-sm text-charcoal mt-2" data-testid="quote-photo-uploaded">
            <CheckCircle2 size={16} className="text-green-600" /> {photo.filename} attached
            <button type="button" onClick={() => { setPhoto(null); const i = document.getElementById("quote-photo-input"); if (i) i.value = ""; }} className="text-gray-500 hover:text-red-500" aria-label="Remove photo" data-testid="quote-photo-remove">
              <X size={14} />
            </button>
          </p>
        )}

        <div className="mt-4">
          <span className="block text-sm font-bold text-charcoal mb-2">How Did You Hear About Us?</span>
          <Select value={form.hear} onValueChange={(v) => setForm({ ...form, hear: v })}>
            <SelectTrigger data-testid="quote-hear-select">
              <SelectValue placeholder="How Did You Hear About Us?" />
            </SelectTrigger>
            <SelectContent>
              {hearOptions.map((o) => (
                <SelectItem key={o} value={o}>{o}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <button type="submit" disabled={sending || uploading} className="btn-fire w-full mt-5 py-3 text-[15px] disabled:opacity-60" data-testid="quote-submit-button">
          {sending ? "Sending..." : "Get My Free Quote"}
        </button>
        <p className="text-[12px] text-gray-500 mt-3 text-center">
          By submitting you agree to our <a href="/privacy" className="underline hover:text-fire">Privacy Policy</a>.
        </p>
      </form>
    </div>
  );
};

export default QuoteCard;
